import React from "react";
import * as yup from "yup";
import { connect } from "react-redux";
import { useFormik } from "formik";
import { withRouter } from "react-router";
import { ModalCharge } from "../../shared/ModalCharge/ModalCharge";
import { svg } from "../../../../../assets/svg";
import { useAppTranslation } from "../../../../../i18n/useAppTranslation";
import { useInvoice } from "../../hooks/invoice/useInvoice";
import { useInvoiceActions } from "../../hooks/useInvoiceActions";
import { checkoutInvoice } from "../../../../../store/checkoutInvoice";
import ViewTipsModal from "../ViewTipsModal/ViewTipsModal";
import { InputLabel } from "../../../../../shared/InputLabel/InputLabel";
import { InputCurrency } from "../../../../../shared/InputCurrency/InputCurrency";
import { useAvailablePaymentMethods } from "../../hooks/useAvailablePaymentMethods";
import { composeAmountSchema } from "../../SalesCheckoutInvoice.schemas";
import { usePatientFi } from "../../hooks/usePatientFi";
import OnGoingPaymentBox from "./components/OnGoingPaymentBox";
import { InputError } from "../../../../../shared/InputError/InputError";
import { uiNotification } from "../../../../../services/UINotificationService";

const ViewChargePatientFi = ({ isTipsApplying, history }) => {
  const { invoice, amountToPay } = useInvoice();
  const { paymentViewClose } = useInvoiceActions();

  const { chargePfi, isLoading, cancelPfiTransaction, onGoingPayment } =
    usePatientFi();

  const { tCommon } = useAppTranslation.Common();
  const { tSales } = useAppTranslation.Sales();

  const { data: pfiData } = useAvailablePaymentMethods(invoice.id, {
    select: (data) => ({
      availableBalance: data.pfi.availableBalance,
      minAmount: data.pfi.minAmouht,
    }),
  });

  const schema = yup.object({
    amount: composeAmountSchema({
      amountToPay: Math.min(pfiData.availableBalance, amountToPay),
      currency: invoice?.currency,
      customMinAmount: pfiData.minAmount,
    }),
  });

  const { values, setFieldValue, handleSubmit, errors, isValid } = useFormik({
    validationSchema: schema,
    initialValues: {
      amount: amountToPay,
    },
    enableReinitialize: true,
    onSubmit: (values) =>
      chargePfi({
        invoice_id: invoice.id,
        requested_payment: parseFloat(values.amount),
      }),
  });

  const handleCancelTransaction = () => {
    cancelPfiTransaction(
      {
        invoice_id: invoice.id,
        requested_payment: parseFloat(values.amount),
      },
      {
        onSuccess: () => {
          onGoingPayment.set(false);
          uiNotification.success("Payment cancelled successfully");
        },
      },
    );
  };

  const isChargeDisabled =
    isTipsApplying || isLoading || onGoingPayment.value || !isValid;

  const onClose = () => {
    const invoiceId = invoice?.id;
    const patientId = invoice?.patient?.id;

    if (onGoingPayment.value) {
      history.push(`/sales/invoice/${invoiceId}/${patientId}/invoices`);
    } else {
      paymentViewClose();
    }
  };

  return (
    <ModalCharge
      isOpen
      onClose={onClose}
      title={tSales("checkoutInvoice.paymentOptions.patientFi")}
      imageSrc={svg.patientFi}
      onCharge={handleSubmit}
      isCharging={isLoading}
      isChargeDisabled={isChargeDisabled}
      chargeLabel={tCommon("label.charge")}
    >
      <ViewTipsModal amountToPay={amountToPay} />
      <ModalCharge.InputWrap>
        <InputLabel>
          {tSales("checkoutInvoice.formInputLabel.amount")}
        </InputLabel>
        <InputCurrency
          size="medium"
          prefix={invoice?.currencySymbol}
          value={values.amount}
          placeholder={tSales("checkoutInvoice.formInputPlaceholder.amount")}
          onChange={(value) => setFieldValue("amount", value)}
          isError={Boolean(errors.amount)}
          isDisabled={onGoingPayment.value}
        />
        {Boolean(errors.amount) && <InputError>{errors.amount}</InputError>}
      </ModalCharge.InputWrap>
      {onGoingPayment.value && (
        <OnGoingPaymentBox
          onCancelPayment={handleCancelTransaction}
          isCancelling={isLoading}
        />
      )}
    </ModalCharge>
  );
};

const mapStateToProps = (state) => ({
  isTipsApplying: checkoutInvoice.selectors.selectIsTipsApplying(state),
});

export default connect(mapStateToProps)(withRouter(ViewChargePatientFi));
